import { SolIcon } from '@sol/icons';
import { SolTag } from '../../../models/core/sol-tag';
import { ObjectTools } from '../../../tools/object-tools';

export interface SolTab {
  id: string;
  name?: string;
  icon?: SolIcon;
  disabled?: boolean;
  translatable?: boolean;
  tag?: SolTag;
}

export interface Tab {
  id: string;
  name: string;
  icon?: SolIcon;
  disabled: boolean;
  selected: boolean;
  translatable: boolean;
  tag: SolTag | undefined;
}

export class SolTabsState {
  private static readonly buttonSize = 40;

  private constructor(
    // Input
    public readonly tabs: SolTab[],
    public readonly selectedTab: string,
    public readonly dividerVisible: boolean,
    public readonly large: boolean,
    public readonly sizeToContent: boolean,

    // Runtime (from resize observers)
    public readonly tabsContainerWidth: number,
    public readonly tabsWidth: number,
    public readonly tabWidths: number[],

    // Runtime (from content)
    public readonly contentVisible: boolean,

    // Derived
    public readonly selectedTabIndex: number,
    public readonly visibleTabs: Tab[],
    public readonly previousOffscreenTabIndex: number,
    public readonly nextOffscreenTabIndex: number,
    public readonly scrollLeft: number,
    public readonly hostClass: string,
    public readonly previousVisible: boolean,
    public readonly nextVisible: boolean,
  ) {}

  public static of(): SolTabsState {
    return new SolTabsState(
      [],
      '',
      true,
      false,
      false,
      0,
      0,
      [],
      false,
      0,
      [],
      -1,
      -1,
      0,
      'sol-tabs-container',
      false,
      false,
    );
  }

  public withTabs(tabs: SolTab[]): SolTabsState {
    return this.copy({ tabs }).recalculate();
  }

  public withSelectedTab(selectedTab: string): SolTabsState {
    return this.copy({ selectedTab }).recalculate();
  }

  public withDividerVisible(dividerVisible: boolean): SolTabsState {
    return this.copy({ dividerVisible }).recalculateHostClass().recalculateDerived();
  }

  public withLarge(large: boolean): SolTabsState {
    return this.copy({ large });
  }

  public withSizeToContent(sizeToContent: boolean): SolTabsState {
    return this.copy({ sizeToContent }).recalculateHostClass();
  }

  public withTabsContainerWidth(width: number): SolTabsState {
    return this.copy({ tabsContainerWidth: width }).recalculateOffscreenTabs().recalculateDerived();
  }

  public withTabsResize(tabsWidth: number, tabWidths: number[]): SolTabsState {
    return this.copy({ tabsWidth, tabWidths }).recalculateOffscreenTabs().recalculateDerived();
  }

  public withContentVisible(contentVisible: boolean): SolTabsState {
    return this.copy({ contentVisible });
  }

  private recalculate(): SolTabsState {
    return this.recalculateSelectedTab()
      .recalculateTabs()
      .recalculateOffscreenTabs()
      .recalculateHostClass()
      .recalculateDerived();
  }

  private recalculateSelectedTab(): SolTabsState {
    const candidateId = this.tabs.find(tab => tab.id === this.selectedTab)?.id;
    const selectedId = candidateId || (this.tabs.length > 0 ? this.tabs[0].id : '');
    return this.copy({
      selectedTab: this.selectedTab === '' ? selectedId : this.selectedTab,
      selectedTabIndex: this.tabs.findIndex(tab => tab.id === selectedId),
    });
  }

  private recalculateTabs(): SolTabsState {
    const visibleTabs: Tab[] = this.tabs.map(tab => ({
      id: tab.id,
      name: tab.name || tab.id,
      icon: tab.icon,
      disabled: tab.disabled || false,
      selected: tab.id === this.selectedTab,
      translatable: tab.translatable || true,
      tag: tab.tag,
    }));
    return this.copy({ visibleTabs });
  }

  private recalculateOffscreenTabs(): SolTabsState {
    const allTabsWidth = this.tabWidths.reduce((prev, cur) => prev + cur, 0);
    if (allTabsWidth <= this.tabsContainerWidth + 1) {
      return this.copy({
        scrollLeft: 0,
        previousOffscreenTabIndex: -1,
        nextOffscreenTabIndex: -1,
      });
    }

    const withScrollLeft = this.calculateScrollLeft();
    const previousOffscreenTabIndex = withScrollLeft.calculatePreviousOffscreenTabIndex();
    const nextOffscreenTabIndex = withScrollLeft.calculateNextOffscreenTabIndex();
    return withScrollLeft.copy({
      previousOffscreenTabIndex,
      nextOffscreenTabIndex,
    });
  }

  private recalculateHostClass(): SolTabsState {
    const hostClass =
      'sol-tabs-container' +
      (!this.dividerVisible ? ' sol-tabs-container-margin' : '') +
      (this.sizeToContent ? ' sol-tabs-container-size-to-content' : '');
    return this.copy({ hostClass });
  }

  private recalculateDerived(): SolTabsState {
    return this.copy({
      previousVisible: this.previousOffscreenTabIndex !== -1,
      nextVisible: this.nextOffscreenTabIndex !== -1,
    });
  }

  private calculateScrollLeft(): SolTabsState {
    const selectedTabCenter = this.calculateSelectedTabCenter();
    const allTabsWidth = this.tabWidths.reduce((prev, cur) => prev + cur, 0);
    const scrollLeft = Math.min(Math.max(0, selectedTabCenter), allTabsWidth - this.tabsContainerWidth);
    return this.copy({ scrollLeft });
  }

  private calculatePreviousOffscreenTabIndex(): number {
    let total = this.scrollLeft > 0 ? -SolTabsState.buttonSize : 0;
    for (let i = 0; i <= this.selectedTabIndex && i < this.tabWidths.length; i++) {
      const tabWidth = this.tabWidths[i];
      if (total < this.scrollLeft && total + tabWidth > this.scrollLeft) {
        return i;
      }
      total += tabWidth;
    }
    return -1;
  }

  private calculateNextOffscreenTabIndex(): number {
    const scrollRight = this.scrollLeft + this.tabsContainerWidth;
    const allTabsWidth = this.tabWidths.reduce((prev, cur) => prev + cur, 0);
    let total = scrollRight < allTabsWidth ? SolTabsState.buttonSize : 0;
    for (let i = 0; i < this.tabWidths.length; i++) {
      const tabWidth = this.tabWidths[i];
      if (total < scrollRight && total + tabWidth > scrollRight) {
        return i;
      }
      total += tabWidth;
    }
    return -1;
  }

  private calculateSelectedTabCenter(): number {
    let total = 0;
    for (let i = 0; i <= this.selectedTabIndex && i < this.tabWidths.length; i++) {
      const tabWidth = this.tabWidths[i];
      total += i === this.selectedTabIndex ? tabWidth / 2 : tabWidth;
    }
    return total - this.tabsContainerWidth / 2;
  }

  private copy(props: Partial<SolTabsState>): SolTabsState {
    return ObjectTools.copy<SolTabsState>(this, props);
  }
}
